import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface FillBlankQuestionProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    answered: boolean;
    submitting: boolean;
    isCorrect: boolean;
    answer: string | number | string[] | number[];
}

function formatAnswer(answer: string | number | string[] | number[]) {
    return Array.isArray(answer) ? answer.join(' → ') : String(answer);
}

export default function FillBlankQuestion({
    value,
    onChange,
    onSubmit,
    answered,
    submitting,
    isCorrect,
    answer,
}: FillBlankQuestionProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    return (
        <div className="space-y-5">
            <div className="border border-[#2d3554] bg-[#111723]">
                <div className="flex items-center justify-between border-b border-[#222b44] bg-[#151d2f] px-4 py-3">
                    <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#8cacff]">
                        KEY-IN TERMINAL
                    </span>
                    <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#767575]">
                        PRESS ENTER TO LOCK
                    </span>
                </div>

                <div className="px-4 py-4">
                    <input
                        ref={inputRef}
                        type="text"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
                        disabled={answered || submitting}
                        placeholder="inject answer payload"
                        className={cn(
                            "w-full border bg-[#0d121d] px-5 py-5 font-['IBM_Plex_Mono'] text-lg uppercase tracking-[0.12em] text-[#dedede] outline-none transition-colors placeholder:text-[#5c6685]",
                            answered
                                ? isCorrect
                                    ? 'border-[#00cffc] bg-[#07181b]'
                                    : 'border-[#ff716c] bg-[#1d1111]'
                                : 'border-[#2d3554] focus:border-[#8cacff]',
                        )}
                    />
                </div>
            </div>

            {!answered && (
                <motion.button
                    whileTap={{ scale: 0.985 }}
                    onClick={onSubmit}
                    disabled={!value.trim() || submitting}
                    className="inline-flex w-full items-center justify-center border border-[#8cacff] bg-[#1a2236] px-6 py-5 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.22em] text-white transition-colors hover:bg-[#222c49] disabled:cursor-not-allowed disabled:border-[#2a2a2a] disabled:bg-[#161616] disabled:text-[#767575]"
                >
                    {submitting ? 'Validating...' : 'Lock Answer'}
                </motion.button>
            )}

            {answered && !isCorrect && (
                <div className="border border-[#ff716c]/25 bg-[#ff716c]/8 px-5 py-4">
                    <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#ff716c]">
                        Correct Payload
                    </p>
                    <p className="mt-3 font-['IBM_Plex_Mono'] text-sm uppercase tracking-[0.16em] text-[#f4c5c2]">
                        {formatAnswer(answer)}
                    </p>
                </div>
            )}
        </div>
    );
}
